// ─── Actions sur les COMPTES (app d'administration) ───────────────
// Portées de MoPCal (services/admin.js) : cycle de vie d'un compte, options,
// groupes IVV. Toutes les écritures sont gardées par les règles Firestore
// (claim `admin`) — l'UI n'est qu'un confort.
import { db, auth, functions } from '@mop/core';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

// ── Cycle de vie ──
// Suppression DOUCE (comme MoPCal) : le compte Auth reste, le profil passe en
// « supprime » → restaurable, et l'historique n'est pas perdu.
export const supprimerCompte = (uid) =>
  updateDoc(doc(db, 'users', uid), { statut: 'supprime', supprimeLe: new Date().toISOString() });
export const restaurerCompte = (uid) =>
  updateDoc(doc(db, 'users', uid), { statut: 'actif', supprimeLe: null });
// Suppression DÉFINITIVE (identité Auth + profil), réservée à un compte déjà en
// corbeille — passe par la Cloud Function `supprimerCompteDefinitif` : le SDK
// client ne peut jamais supprimer le compte Auth d'un AUTRE utilisateur.
export const supprimerCompteDefinitif = (uid) =>
  httpsCallable(functions, 'supprimerCompteDefinitif')({ uid });
export const approuverCompte = (uid) =>
  updateDoc(doc(db, 'users', uid), { statut: 'actif', approuveLe: new Date().toISOString() });
export const rejeterCompte = (uid) =>
  updateDoc(doc(db, 'users', uid), { statut: 'rejete', rejeteLe: new Date().toISOString() });

// Lien de réinitialisation de mot de passe (envoyé par Firebase Auth).
export const envoyerLienMotDePasse = (email) => sendPasswordResetEmail(auth, email);

// ── Options d'un compte ──
export const definirSansPub = (uid, v) => updateDoc(doc(db, 'users', uid), { noPub: !!v });
export const definirAccesIllimite = (uid, v) => updateDoc(doc(db, 'users', uid), { accesIllimite: !!v });

// ── Groupe IVV : plusieurs comptes qui PARTAGENT une identité de valideur ──
export const definirGroupeIVV = (uid, nom) =>
  updateDoc(doc(db, 'users', uid), { groupeIVV: (nom || '').trim() || null });
export async function renommerGroupeIVV(uids, nom) {
  const n = (nom || '').trim() || null;
  for (const uid of uids || []) {
    try { await updateDoc(doc(db, 'users', uid), { groupeIVV: n }); } catch (_) {}
  }
}

// ── Création d'un compte depuis le panneau ──
// L'API REST `accounts:signUp` crée le compte SANS déconnecter l'admin (une
// création via le SDK basculerait la session courante). Le profil Firestore est
// écrit ensuite, directement actif.
export async function creerCompte({ email, motDePasse, prenom, plan = 'gratuit' }) {
  const cle = auth?.app?.options?.apiKey;
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${cle}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), password: motDePasse, returnSecureToken: false }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'Création impossible');
  await setDoc(doc(db, 'users', data.localId), {
    prenom: (prenom || '').trim(), email: data.email, plan, statut: 'actif',
    creeLe: new Date().toISOString(), creeParAdmin: true,
  });
  return { uid: data.localId, email: data.email };
}

// ── E-mails AUTORISÉS à s'inscrire (config/inscription.emails) ──
export function ecouterEmailsAutorises(callback) {
  return onSnapshot(doc(db, 'config', 'inscription'),
    (snap) => callback(snap.exists() ? (snap.data().emails || []) : []),
    () => callback([]));
}
const listeEmails = async () => {
  const snap = await getDoc(doc(db, 'config', 'inscription'));
  return snap.exists() ? (snap.data().emails || []) : [];
};
export async function ajouterEmailAutorise(email) {
  const e = (email || '').trim().toLowerCase();
  const emails = await listeEmails();
  if (!e || emails.some(x => (x.email || '').toLowerCase() === e)) return false;
  await setDoc(doc(db, 'config', 'inscription'),
    { emails: [...emails, { email: e, actif: true, ajouteLe: new Date().toISOString() }] }, { merge: true });
  return true;
}
export async function basculerEmailAutorise(email, actif) {
  const emails = await listeEmails();
  await setDoc(doc(db, 'config', 'inscription'), {
    emails: emails.map(x => ((x.email || '').toLowerCase() === (email || '').toLowerCase() ? { ...x, actif } : x)),
  }, { merge: true });
}
export async function supprimerEmailAutorise(email) {
  const emails = await listeEmails();
  await setDoc(doc(db, 'config', 'inscription'), {
    emails: emails.filter(x => (x.email || '').toLowerCase() !== (email || '').toLowerCase()),
  }, { merge: true });
}

// ── Mode d'inscription (approbation manuelle par défaut) ──
export function ecouterModeInscription(callback) {
  return onSnapshot(doc(db, 'config', 'inscription'),
    (snap) => callback(snap.exists() ? !!snap.data().approbationAuto : false),
    () => callback(false));
}
export async function setApprobationAuto(auto) {
  await setDoc(doc(db, 'config', 'inscription'), { approbationAuto: !!auto }, { merge: true });
}

// ── E-mails INTERDITS (liste noire, config/inscription.interdits) ──
export function ecouterEmailsInterdits(callback) {
  return onSnapshot(doc(db, 'config', 'inscription'),
    (snap) => callback(snap.exists() ? (snap.data().interdits || []) : []),
    () => callback([]));
}
// Bloque l'adresse (retirée des autorisés si elle y était).
export async function interdireEmail(email) {
  const ref = doc(db, 'config', 'inscription');
  const d = (await getDoc(ref)).data() || {};
  const e = (email || '').trim().toLowerCase();
  const interdits = (d.interdits || []).filter(x => (x.email || '').toLowerCase() !== e);
  interdits.push({ email: e, ajouteLe: new Date().toISOString() });
  const emails = (d.emails || []).filter(x => (x.email || '').toLowerCase() !== e);
  await setDoc(ref, { interdits, emails }, { merge: true });
}
// Autorise l'adresse (ajoute ou réactive dans les autorisés, retire des interdits).
export async function autoriserEmail(email) {
  const ref = doc(db, 'config', 'inscription');
  const d = (await getDoc(ref)).data() || {};
  const e = (email || '').trim().toLowerCase();
  const interdits = (d.interdits || []).filter(x => (x.email || '').toLowerCase() !== e);
  let emails = d.emails || [];
  emails = emails.some(x => (x.email || '').toLowerCase() === e)
    ? emails.map(x => ((x.email || '').toLowerCase() === e ? { ...x, actif: true } : x))
    : [...emails, { email: e, actif: true, ajouteLe: new Date().toISOString() }];
  await setDoc(ref, { interdits, emails }, { merge: true });
}
export async function retirerInterdit(email) {
  const ref = doc(db, 'config', 'inscription');
  const e = (email || '').trim().toLowerCase();
  const interdits = ((await getDoc(ref)).data()?.interdits || []).filter(x => (x.email || '').toLowerCase() !== e);
  await updateDoc(ref, { interdits });
}

// ── Listes ADMIN / COMMERCE (config/admin) ──
export function ecouterConfigAdmin(callback) {
  return onSnapshot(doc(db, 'config', 'admin'),
    (snap) => {
      const d = snap.exists() ? snap.data() : {};
      callback({ emails: d.emails || [], commerce: d.commerce || [] });
    },
    () => callback({ emails: [], commerce: [] }));
}

// Donne un rôle d'administration à partir de l'E-MAIL. Écrire `config/admin`
// suffit : la Cloud Function `majClaimsConfigAdmin` (déclencheur sur ce doc)
// recalcule les claims de tous les comptes concernés.
export async function ajouterRoleParEmail(email, type, monEmail) {
  const e = String(email || '').trim().toLowerCase();
  if (!e) throw new Error('Adresse manquante.');
  const snap = await getDoc(doc(db, 'config', 'admin'));
  const cfg = snap.exists() ? snap.data() : {};
  const cle = type === 'commerce' ? 'commerce' : 'emails';
  const liste = cfg[cle] || [];
  if (liste.map(x => String(x).toLowerCase()).includes(e)) return false;
  await setDoc(doc(db, 'config', 'admin'), { [cle]: [...liste, e] }, { merge: true });
  return true;
}

// Retire un rôle d'administration à partir de l'E-MAIL (l'uid n'est pas connu
// depuis la liste). Écrire `config/admin` suffit : la Cloud Function
// `majClaimsConfigAdmin` (déclencheur sur ce doc) recalcule les claims de tous.
// Garde-fou des règles : l'auteur doit RESTER dans `emails`.
export async function retirerRoleParEmail(email, type, monEmail) {
  const bas = String(email || '').toLowerCase();
  const snap = await getDoc(doc(db, 'config', 'admin'));
  const cfg = snap.exists() ? snap.data() : {};
  const filtre = (l) => (l || []).filter(x => String(x).toLowerCase() !== bas);
  const emails = type === 'admin' ? filtre(cfg.emails) : (cfg.emails || []);
  if (type === 'admin' && !emails.length) throw new Error('Dernier administrateur : retrait impossible.');
  if (monEmail && !emails.map(x => String(x).toLowerCase()).includes(String(monEmail).toLowerCase())) {
    throw new Error('Tu dois rester administrateur.');
  }
  await setDoc(doc(db, 'config', 'admin'), {
    emails, commerce: type === 'commerce' ? filtre(cfg.commerce) : (cfg.commerce || []),
  }, { merge: true });
}
